import { useAuthStore } from '../stores';
import type { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import type { InternalAxiosRequestConfig } from 'axios';

/**
 * Gets the currently selected organization ID from the Zustand store.
 * This is a pure function that can be used outside of React context.
 *
 * @returns The selected organization ID or null if not set
 */
const getSelectedOrgId = (): string | null =>
    // Access the store state directly without React hook
    useAuthStore.getState().selectedOrgId;
/**
 * Creates an axios request interceptor that adds organization-aware auth token.
 * Automatically includes the organization parameter when a selected org exists in the Zustand store.
 *
 * @param getAccessTokenSilently - Auth0 function to get access token
 * @returns Axios request interceptor function
 *
 */
export const createAuthInterceptor =
    (
        getAccessTokenSilently: (
            options?: GetTokenSilentlyOptions,
        ) => Promise<string>,
    ) =>
    async (
        config: InternalAxiosRequestConfig,
    ): Promise<InternalAxiosRequestConfig> => {
        const orgId = getSelectedOrgId();

        const token = await getAccessTokenSilently({
            ...(orgId && {
                authorizationParams: {
                    organization: orgId,
                },
            }),
        });

        config.headers.set('Authorization', `Bearer ${token}`);
        config.withCredentials = true;
        return config;
    };

/**
 * Gets an access token with organization context.
 * This is a pure utility function that can be used outside of React context.
 *
 * @param getAccessTokenSilently - Auth0 function to get access token
 * @returns Promise resolving to the access token
 * @throws Error if token retrieval fails
 */
export const getTokenWithOrgContext = async (
    getAccessTokenSilently: (
        options?: GetTokenSilentlyOptions,
    ) => Promise<string>,
): Promise<string> => {
    const orgId = getSelectedOrgId();

    return getAccessTokenSilently({
        ...(orgId && {
            authorizationParams: {
                organization: orgId,
            },
        }),
    });
};

/**
 * Error codes thrown by Auth0 when the session can no longer be renewed
 * and the user must log in again.
 */
const SESSION_EXPIRED_ERROR_CODES: string[] = [
    'login_required',
    'missing_refresh_token',
    'invalid_grant',
    'consent_required',
    'interaction_required',
    'access_denied',
];

/**
 * Determines whether an error thrown while acquiring a token means the
 * Auth0 session has expired (or is otherwise unusable) and the user must
 * log in again.
 *
 * @param error - Error thrown by getAccessTokenSilently
 * @returns True when the error indicates an expired/unusable session
 */
export const isSessionExpiredError = (error: unknown): boolean => {
    if (typeof error !== 'object' || error === null) {
        return false;
    }

    const code = (error as { error?: unknown }).error;

    return (
        typeof code === 'string' && SESSION_EXPIRED_ERROR_CODES.includes(code)
    );
};
